<template>
  <div id="BookInfo">
    <section class="book-info-section">
      <div class="book-info-container">
        <BreadCrumb :book="book" />

        <div v-if="isLoading" class="book-state">
          {{ $t("common.loading") }}
        </div>

        <div v-else-if="loadError" class="book-state book-state--error">
          {{ loadError }}
        </div>

        <template v-else-if="book?.id">
          <div class="book-detail-card">
            <div class="book-hero">
              <div class="book-gallery">
                <button
                  v-if="activeImage"
                  type="button"
                  class="book-main-image"
                  :aria-label="displayTitle"
                  @click="isLightboxOpen = true"
                >
                  <img
                    :src="activeImage"
                    :alt="displayTitle"
                    class="book-cover"
                    :class="{
                      'book-cover--hero': activeImage === heroImage,
                      'book-cover--cover': activeImage !== heroImage,
                    }"
                  />
                </button>

                <div v-else class="book-main-image book-main-image--empty">
                  <span>No image</span>
                </div>

                <div
                  v-if="coverImages.length"
                  class="book-thumbnail-carousel"
                  aria-label="教材封面"
                >
                  <button
                    v-if="coverImages.length > 1"
                    type="button"
                    class="thumbnail-arrow thumbnail-arrow--left"
                    aria-label="上一張封面"
                    @click="scrollThumbnails(-1)"
                  >
                    ‹
                  </button>

                  <div
                    ref="thumbnailTrack"
                    class="book-thumbnails"
                    role="list"
                    @wheel="handleThumbnailWheel"
                  >
                    <button
                      v-for="(image, index) in coverImages"
                      :key="`${image}-${index}`"
                      type="button"
                      class="book-thumbnail"
                      :class="{ 'is-active': image === activeImage }"
                      :aria-label="`${displayTitle} 封面 ${index + 1}`"
                      :aria-pressed="image === activeImage"
                      role="listitem"
                      @click="activeImage = image"
                    >
                      <img
                        :src="image"
                        :alt="`${displayTitle} 封面 ${index + 1}`"
                      />
                    </button>
                  </div>

                  <button
                    v-if="coverImages.length > 1"
                    type="button"
                    class="thumbnail-arrow thumbnail-arrow--right"
                    aria-label="下一張封面"
                    @click="scrollThumbnails(1)"
                  >
                    ›
                  </button>

                  <div
                    v-if="coverImages.length > 1"
                    class="thumbnail-dots"
                    aria-label="封面位置"
                  >
                    <button
                      v-for="(image, index) in coverImages"
                      :key="`dot-${image}-${index}`"
                      type="button"
                      class="thumbnail-dot"
                      :class="{ 'is-active': image === activeImage }"
                      :aria-label="`切換至第 ${index + 1} 張封面`"
                      :aria-pressed="image === activeImage"
                      @click="selectCover(image, index)"
                    ></button>
                  </div>
                </div>
              </div>

              <div class="book-copy">
                <h3 class="book-title">{{ displayTitle }}</h3>

                <p v-if="displaySubtitle" class="book-subtitle">
                  {{ displaySubtitle }}
                </p>

                <p
                  v-if="displaySummary"
                  class="book-summary"
                  v-html="formatSummary(displaySummary)"
                ></p>
                <div v-if="metaItems.length" class="book-meta-list">
                  <div
                    v-for="item in metaItems"
                    :key="item.key"
                    class="book-meta-item"
                  >
                    <span class="book-meta-icon" aria-hidden="true">
                      <svg
                        v-if="item.key === 'grade'"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      </svg>

                      <svg
                        v-else-if="item.key === 'cefr'"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="m12 2 2.9 5.88 6.49.94-4.7 4.58 1.11 6.47L12 16.82l-5.8 3.05 1.11-6.47-4.7-4.58 6.49-.94L12 2Z"
                        />
                      </svg>

                      <svg
                        v-else-if="item.key === 'type'"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                        <path
                          d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"
                        />
                      </svg>

                      <svg v-else viewBox="0 0 24 24" fill="none">
                        <path d="M3 21h18" />
                        <path d="M6 21V7l6-4 6 4v14" />
                        <path d="M9 21v-6h6v6" />
                      </svg>
                    </span>

                    <span class="book-meta-copy">
                      <span class="book-meta-label">{{ item.label }}</span>
                      <strong>{{ item.value }}</strong>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <BookContent />
          </div>
        </template>

        <div v-else class="book-state">
          {{ notFoundText }}
        </div>
      </div>
    </section>

    <div
      v-if="isLightboxOpen && activeImage"
      class="book-lightbox"
      role="dialog"
      aria-modal="true"
      @click.self="isLightboxOpen = false"
    >
      <div class="image-lightbox-panel">
        <button
          type="button"
          class="book-lightbox-close"
          aria-label="Close"
          @click="isLightboxOpen = false"
        >
          ×
        </button>
        <img
          class="image-lightbox-content"
          :src="activeImage"
          :alt="displayTitle"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { useRoute } from "vue-router";
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import BookContent from "./BookContent.vue";
import BreadCrumb from "./BreadCrumb.vue";

export default {
  name: "BookInfo",
  components: {
    BookContent,
    BreadCrumb,
  },
  emits: ["book-loaded"],

  setup(_, { emit }) {
    const route = useRoute();
    const { proxy } = getCurrentInstance();

    const book = ref({});
    const activeImage = ref("");
    const thumbnailTrack = ref(null);
    const isLoading = ref(true);
    const loadError = ref("");
    const isLightboxOpen = ref(false);

    const currentLang = computed(() => proxy?.$i18n?.locale || "zh");
    const bookPageId = computed(() => route.params.BookPageId || "");
    const sheetName = computed(() => route.params.IconButtonId || "");

    const getText = (zh, en) =>
      currentLang.value === "zh" ? zh || en || "" : en || zh || "";

    const normalizeImageUrl = (value) => {
      const image = String(value || "").trim();
      if (!image) return "";
      if (/^(https?:)?\/\//i.test(image) || image.startsWith("data:")) {
        return image;
      }
      return `${process.env.BASE_URL}${image.replace(/^\//, "")}`;
    };

    // 頁面主標題固定使用 Google Sheet 的 title_en。
    // id 只負責網址與資料定位，不拿來顯示或製作 SEO 標題。
    const displayTitle = computed(() =>
      String(book.value.title_en || book.value.title_zh || "").trim(),
    );

    const displayCategory = computed(() =>
      getText(book.value.category_zh, book.value.category_en),
    );

    const displayGrade = computed(() =>
      getText(book.value.grade_zh, book.value.grade_en),
    );

    const displayPublisher = computed(() =>
      getText(book.value.publish_zh, book.value.publish_en),
    );

    const displaySummary = computed(() =>
      getText(book.value.summary_zh, book.value.summary_en),
    );

    const formatSummary = (text) => {
      if (!text) return "";

      return String(text)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br>");
    };

    const displaySubtitle = computed(() =>
      getText(book.value.subtitle_zh, book.value.subtitle_en),
    );

    const displayCefr = computed(() =>
      getText(
        book.value.cefr_zh || book.value.cefr,
        book.value.cefr_en || book.value.cefr,
      ),
    );

    const labels = computed(() => ({
      grade: currentLang.value === "zh" ? "適用年級" : "Grade",
      cefr: "CEFR",
      type: currentLang.value === "zh" ? "教材類型" : "Category",
      publisher: currentLang.value === "zh" ? "出版社" : "Publisher",
    }));

    const metaItems = computed(() =>
      [
        { key: "grade", label: labels.value.grade, value: displayGrade.value },
        { key: "cefr", label: labels.value.cefr, value: displayCefr.value },
        { key: "type", label: labels.value.type, value: displayCategory.value },
        {
          key: "publisher",
          label: labels.value.publisher,
          value: displayPublisher.value,
        },
      ].filter((item) => item.value),
    );

    const splitImageValues = (value) =>
      String(value || "")
        .split(/\n|,/)
        .map(normalizeImageUrl)
        .filter(Boolean);

    const heroImage = computed(() => {
      const explicitHero = splitImageValues(
        book.value.heroImg || book.value.hero_img || book.value.heroImage,
      )[0];

      if (explicitHero) return explicitHero;

      return (
        splitImageValues(
          book.value.img ||
            book.value.image ||
            book.value.img01 ||
            book.value.image01,
        )[0] || ""
      );
    });

    const coverImages = computed(() => {
      const explicitCovers = [
        ...splitImageValues(book.value.coverImgs),
        ...splitImageValues(book.value.cover_imgs),
        ...splitImageValues(book.value.coverImages),
      ];

      if (explicitCovers.length) {
        return [...new Set(explicitCovers)];
      }

      const legacyKeys = [
        "img",
        "img01",
        "img02",
        "img03",
        "img04",
        "image",
        "image01",
        "image02",
        "image03",
        "image04",
      ];

      const legacyImages = legacyKeys.flatMap((key) =>
        splitImageValues(book.value[key]),
      );

      const uniqueImages = [...new Set(legacyImages)];

      if (heroImage.value && uniqueImages[0] === heroImage.value) {
        return uniqueImages.slice(1);
      }

      return uniqueImages;
    });

    const galleryImages = computed(() => [
      ...new Set([heroImage.value, ...coverImages.value].filter(Boolean)),
    ]);

    const scrollThumbnails = (direction) => {
      const track = thumbnailTrack.value;
      if (!track) return;

      const firstItem = track.querySelector(".book-thumbnail");
      const step = (firstItem?.offsetWidth || 92) + 12;

      track.scrollBy({
        left: direction * step,
        behavior: "smooth",
      });
    };

    const selectCover = (image, index) => {
      activeImage.value = image;

      const track = thumbnailTrack.value;
      const target = track?.children?.[index];
      target?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    };

    const handleThumbnailWheel = (event) => {
      const track = thumbnailTrack.value;
      if (!track || track.scrollWidth <= track.clientWidth) return;

      event.preventDefault();
      track.scrollLeft += event.deltaY || event.deltaX;
    };

    watch(
      galleryImages,
      (images) => {
        if (!images.includes(activeImage.value)) {
          activeImage.value = heroImage.value || coverImages.value[0] || "";
        }
      },
      { immediate: true },
    );

    const formatArrayDataToObject = (dataList) => {
      const headers = dataList[0];
      if (!headers) return {};

      for (let rowIndex = 1; rowIndex < dataList.length; rowIndex += 1) {
        const row = dataList[rowIndex];
        const item = {};

        headers.forEach((header, columnIndex) => {
          const key = String(header || "").trim();
          if (key) item[key] = row[columnIndex] || "";
        });

        if (String(item.id) === String(bookPageId.value)) {
          return item;
        }
      }

      return {};
    };

    const fetchData = async () => {
      isLoading.value = true;
      loadError.value = "";

      try {
        if (!sheetName.value) throw new Error("Missing sheet name");

        const sheetID = "1MHtAg-CNswJOMl7w0ddtfPir4ERH6Q5M_AAd-80P2vc";
        const apiKey = "AIzaSyD21a6U3dc4wpee6BCu4D-pxnPff9QSW4w";
        const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetID}/values/${encodeURIComponent(
          sheetName.value,
        )}?alt=json&key=${apiKey}`;

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Google Sheet request failed: ${response.status}`);
        }

        const data = await response.json();
        book.value = formatArrayDataToObject(data.values || []);

        // 將完整教材資料傳給 BookPage.vue，
        // 由父層使用 title_en 更新 SEO title、description 與 Open Graph。
        if (book.value?.id) {
          emit("book-loaded", book.value);
        }
      } catch (error) {
        console.error("BookInfo fetch error:", error);
        book.value = {};
        loadError.value =
          currentLang.value === "zh"
            ? "教材資料載入失敗，請稍後再試。"
            : "Unable to load this book. Please try again later.";
      } finally {
        isLoading.value = false;
      }
    };

    const onKeydown = (event) => {
      if (event.key === "Escape") isLightboxOpen.value = false;
    };

    onMounted(() => {
      fetchData();
      window.addEventListener("keydown", onKeydown);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("keydown", onKeydown);
    });

    watch(
      () => [route.params.BookPageId, route.params.IconButtonId],
      fetchData,
    );

    const notFoundText = computed(() =>
      currentLang.value === "zh"
        ? "找不到此教材資料。"
        : "This book could not be found.",
    );

    return {
      book,
      activeImage,
      thumbnailTrack,
      heroImage,
      coverImages,
      galleryImages,
      scrollThumbnails,
      selectCover,
      handleThumbnailWheel,
      isLoading,
      loadError,
      isLightboxOpen,
      displayTitle,
      displaySubtitle,
      displaySummary,
      formatSummary, 
      metaItems,
      notFoundText,
    };
  },
};
</script>

<style scoped>
.book-info-section {
  padding: 28px 0 76px;
  background: #f6f8fb;
}

.book-info-container {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
}

.book-detail-card {
  margin-top: 22px;
  overflow: hidden;
  border: 1px solid #e4eaf0;
  border-radius: 24px;
  background: #fff;
  box-shadow: 0 18px 50px rgba(30, 52, 74, 0.08);
}

.book-hero {
  display: grid;
  grid-template-columns: minmax(300px, 0.92fr) minmax(0, 1.08fr);
  gap: clamp(36px, 6vw, 76px);
  align-items: center;
  padding: clamp(28px, 4vw, 52px);
  background: transparent;
}

.book-gallery {
  min-width: 0;
  width: 100%;
}

.book-main-image {
  display: grid;
  width: 100%;
  /* height: 300px; */
  /* padding: 24px; */
  overflow: hidden;
  place-items: center;
  box-sizing: border-box;
  border: 0;
  border-radius: 18px;
  cursor: zoom-in;
}

.book-main-image--empty {
  color: #8994a0;
  cursor: default;
}

.book-cover {
  display: block;
  object-fit: contain;
  object-position: center;
  transition: transform 0.25s ease;
}

/* ===========================
   Hero（整套教材圖）
=========================== */

.book-cover--hero {
  width: 92%;
  max-width: 560px;

  max-height: 330px;
  height: auto;
}

/* ===========================
   Cover（單本封面）
=========================== */

.book-cover--cover {
  /* width: 40%; */
  max-width: 320px;
  max-height: 100%;
}

.book-main-image:hover .book-cover {
  transform: scale(1.015);
}

.book-thumbnail-carousel {
  position: relative;
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) 38px;
  grid-template-rows: auto auto;
  gap: 10px;
  align-items: center;
  width: 100%;
  margin-top: 16px;
}

.book-thumbnails {
  display: flex;
  gap: 12px;
  width: 100%;
  padding: 4px 2px 8px;
  overflow-x: auto;
  overflow-y: hidden;
  box-sizing: border-box;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  overscroll-behavior-inline: contain;
}

.book-thumbnails::-webkit-scrollbar {
  display: none;
}

.thumbnail-arrow {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 1px solid #e0e7ed;
  border-radius: 50%;
  background: #fff;
  color: #24618d;
  box-shadow: 0 6px 18px rgba(30, 52, 74, 0.12);
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.thumbnail-arrow:hover {
  transform: translateY(-1px);
  box-shadow: 0 9px 22px rgba(30, 52, 74, 0.17);
}

.thumbnail-arrow:focus-visible {
  outline: 3px solid rgba(36, 97, 141, 0.25);
  outline-offset: 2px;
}

.thumbnail-dots {
  grid-column: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  min-height: 18px;
  margin-top: 2px;
}

.thumbnail-dot {
  width: 7px;
  height: 7px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: #c8d3dc;
  cursor: pointer;
  transition: width 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.thumbnail-dot:hover {
  transform: scale(1.15);
  background: #8eabc1;
}

.thumbnail-dot.is-active {
  width: 24px;
  background: #24618d;
}

.thumbnail-dot:focus-visible {
  outline: 3px solid rgba(36, 97, 141, 0.22);
  outline-offset: 3px;
}

.book-thumbnail {
  display: grid;
  width: 92px;
  height: 128px;
  padding: 7px;
  overflow: hidden;
  flex: 0 0 92px;
  place-items: center;
  box-sizing: border-box;
  scroll-snap-align: start;
  border: 2px solid transparent;
  border-radius: 5px;
  background: #fff;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.book-thumbnail:hover {
  transform: translateY(-2px);
}

.book-thumbnail:focus-visible {
  outline: 3px solid rgba(36, 97, 141, 0.28);
  outline-offset: 2px;
}

.book-thumbnail.is-active {
  border-color: #24618d;
  box-shadow: none;
}

.book-thumbnail img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.book-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  text-align: left;
}

.book-title {
  margin: 0 0 10px 0;
  color: #17324a;
  font-size: 33px;
  font-weight: 760;
  line-height: 1.15;
  letter-spacing: -0.03em;
}

.book-subtitle {
  margin: 0 0 10px 0;
  color: #657486;
  font-size: clamp(17px, 2vw, 22px);
  line-height: 1.5;
}

.book-summary {
  margin: 0;
  color: #4e5e6d;
  font-size: 16px;
  line-height: 1.9;
  white-space: pre-line;
  height: auto;
  min-height: 0;
  overflow-wrap: anywhere;
}

.book-meta-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px 28px;
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #dfe7ed;
  clear: both;
}

.book-meta-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 118px;
  flex: 1 1 120px;
}

.book-meta-icon {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  color: #24618d;
}

.book-meta-icon svg {
  width: 23px;
  height: 23px;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.book-meta-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.book-meta-label {
  color: #7b8894;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.book-meta-copy strong {
  color: #263d50;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.book-state {
  padding: 76px 24px;
  border-radius: 20px;
  background: #fff;
  color: #687583;
  text-align: center;
}

.book-state--error {
  color: #a23a3a;
}

.book-lightbox {
  position: fixed;
  z-index: 99999;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background: rgba(0, 0, 0, 0.78);
}

.book-lightbox-close {
  position: absolute;
  z-index: 100000;
  top: -18px;
  right: -18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 2px solid rgba(23, 79, 125, 0.12);
  border-radius: 50%;
  background: #fff;
  color: #174f7d;
  font-family: Arial, sans-serif;
  font-size: 30px;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.32);
}

.book-lightbox-close:hover {
  background: #f1f6f9;
  transform: scale(1.05);
}

.book-lightbox-close:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.9);
  outline-offset: 3px;
}

@media (max-width: 900px) {
  .book-hero {
    grid-template-columns: 1fr;
    gap: 34px;
  }

  .book-gallery {
    width: min(100%, 560px);
    margin: 0 auto;
  }

  .book-main-image {
    height: 390px;
  }
}

@media (max-width: 600px) {
  .book-info-container {
    width: min(100% - 24px, 1180px);
  }

  .book-detail-card {
    border-radius: 18px;
  }

  .book-hero {
    padding: 20px;
  }

  .book-main-image {
    height: 310px;
    padding: 15px;
  }

  .book-thumbnail-carousel {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto;
  }

  .thumbnail-dots {
    grid-column: 1;
  }

  .thumbnail-arrow {
    display: none;
  }

  .book-thumbnails {
    gap: 10px;
  }

  .book-thumbnail {
    width: 78px;
    height: 108px;
    flex-basis: 78px;
  }

  .book-title {
    font-size: 30px;
  }

  .book-summary {
    margin-top: 20px;
    font-size: 15px;
    line-height: 1.8;
  }

  .book-meta-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px;
  }

  .book-meta-item {
    min-width: 0;
  }
}

@media (max-width: 390px) {
  .book-meta-list {
    grid-template-columns: 1fr;
  }
}

.image-lightbox-panel {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 92vw;
  max-height: 88vh;
}

.image-lightbox-content {
  display: block;
  width: auto;
  max-width: 92vw;
  max-height: 88vh;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.35);
}
</style>
